import React from 'react';
import { withLayout } from 'src/components/Layout';
import { Article, ArticlesApi } from 'src/services/articles';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import ArticleDetailPageView from 'src/containers/Article/Detail/ArticleDetailPage';
import { NextSeo } from 'next-seo';
import * as Sentry from '@sentry/nextjs';
import { imageUrlMaker } from 'src/utils/image-helpers';
import withGlobalData from 'src/hocs/with-global-data';
import { PAGE_REVALIDATE_TIME } from 'src/constants/global';
import ErrorPage from 'src/components/ErrorPage';
import useSWR, { SWRConfig } from 'swr';

type ErrorProps = { error: boolean };
type ArticleProps = {
  title: string;
  description: string;
  fallback: Record<string, any>;
  slug: string;
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    fallback: true,
    paths: [],
  };
};

export const getStaticProps: GetStaticProps = withGlobalData<
  ArticleProps | ErrorProps
>(
  async context => {
    const { params } = context;
    const slug = params?.slug as string;

    try {
      const articlesApi = new ArticlesApi();

      const { data: article } = await articlesApi.getArticleBySlug(slug);

      if (!article) {
        // sanity check
        return {
          props: {
            error: true,
          },
        };
      }

      const relatedArticlesResponse = await articlesApi
        .searchArticles(article.tags.map(tag => tag.slug))
        .catch(() => {
          // We take over here not to catch in the wrapping try catch
          // We do this to fail silently fail if the related articles search failed
          Sentry.captureException('Error searching for related articles');
          return null;
        });

      const relatedArticles = relatedArticlesResponse
        ? relatedArticlesResponse?.data.items
        : null;

      return {
        props: {
          title: article.title,
          description: article.summary,
          fallback: {
            [`article-${slug}`]: { data: article },
            [`related-${slug}`]: { data: relatedArticles },
          },
          slug,
        },
        revalidate: PAGE_REVALIDATE_TIME,
      };
    } catch (e: unknown) {
      Sentry.captureException(e);
      return {
        props: {
          error: true,
        },
      };
    }
  },
  { numOfAdverts: 3 },
);

function ArticleDetailPage(_props: Props) {
  const props = 'error' in _props ? undefined : _props;
  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }
  const slug = props?.slug || (router.query.slug as string);

  if (slug) {
    return (
      <SWRConfig value={{ fallback: props?.fallback }}>
        <ArticleDetails slug={slug} />
      </SWRConfig>
    );
  }

  return null;
}

type Props = ArticleProps | ErrorProps;

const ArticleDetails = ({ slug }: { slug: string }) => {
  const slugKey = `article-${slug}`;
  const searchSlugKey = `related-${slug}`;
  const articlesApi = new ArticlesApi();
  const router = useRouter();

  const { data: articleData, error } = useSWR(slugKey, () =>
    articlesApi.getArticleBySlug(slug),
  );

  const { data: searchArticles } = useSWR(searchSlugKey, () => {
    const tagSlugs = articleData?.data?.tags.map(tag => tag.slug);

    if (tagSlugs) {
      return articlesApi.searchArticles(tagSlugs);
    }
  });

  const article = articleData?.data;
  const relatedArticles = searchArticles?.data?.items?.filter(
    (art: Article) => art.id !== article?.id,
  );

  console.log({ isFallback: router.isFallback, loading: !article });

  if (error) {
    return (
      <ErrorPage message={'This page could not be loaded please try again.'} />
    );
  }

  if (router.isFallback && !article) {
    return <div>Loading...</div>;
  }

  if (!article) return null;
  const crop = article?.landscapeWideCrop;

  const imageUrl =
    article?.image?.url &&
    imageUrlMaker({
      imageUrl: article.image.url,
      cropType: 'landscapeCoverCrop',
      crop,
    });

  return (
    <>
      <NextSeo
        title={article.title}
        description={article.summary}
        twitter={{
          cardType: 'summary_large_image',
        }}
        openGraph={{
          title: article.title,
          description: article.summary,
          images: article.image
            ? [
                {
                  url: imageUrl ?? '',
                },
              ]
            : [],
        }}
      />

      <ArticleDetailPageView
        article={article}
        relatedArticles={relatedArticles}
      />
    </>
  );
};

export default withLayout(ArticleDetailPage as any);
